import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';

const CalendarPage = () => {
  const { getToken } = useAuth();
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showEventModal, setShowEventModal] = useState(false);
  const [showEventDetailModal, setShowEventDetailModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    type: 'meeting',
    attendeeEmail: ''
  });

  useEffect(() => {
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchEvents = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://website-476522.uc.r.appspot.com';
      const response = await fetch(`${apiUrl}/api/admin/calendar/events`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setEvents(data);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://website-476522.uc.r.appspot.com';
      const response = await fetch(`${apiUrl}/api/admin/calendar/events`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newEvent)
      });

      if (response.ok) {
        fetchEvents();
        setShowEventModal(false);
        setNewEvent({ 
          title: '', 
          description: '', 
          date: getTodayDate(), 
          time: getCurrentTime(), 
          type: 'meeting', 
          attendeeEmail: '' 
        });
      }
    } catch (error) {
      console.error('Error creating event:', error);
    }
  };

  const handleEventClick = async (event) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://website-476522.uc.r.appspot.com';
      const response = await fetch(`${apiUrl}/api/admin/calendar/events/${event.googleEventId || event.id}`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (response.ok) {
        const eventDetails = await response.json();
        setSelectedEvent(eventDetails);
        setShowEventDetailModal(true);
      }
    } catch (error) {
      console.error('Error fetching event details:', error);
    }
  };

  const handleRSVP = async (responseStatus) => {
    if (!selectedEvent) return;
    
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://website-476522.uc.r.appspot.com';
      const response = await fetch(`${apiUrl}/api/admin/calendar/events/${selectedEvent.googleEventId || selectedEvent.id}/rsvp`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ responseStatus })
      });

      if (response.ok) {
        // Refresh events and close modal
        fetchEvents();
        setShowEventDetailModal(false);
        setSelectedEvent(null);
      }
    } catch (error) {
      console.error('Error updating RSVP:', error);
    }
  };

  // Placeholder kept for future integration (removed unused var)

  const startOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
  const endOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
  const startDayOfWeek = startOfMonth.getDay();
  const daysInMonth = endOfMonth.getDate();

  const prevMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  const nextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  const today = () => setCurrentMonth(new Date());

  const formatDateKey = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString().slice(0,10);
  const eventsByDate = events.reduce((acc, ev) => {
    const key = typeof ev.date === 'string' ? ev.date : formatDateKey(new Date(ev.date));
    if (!acc[key]) acc[key] = [];
    acc[key].push(ev);
    return acc;
  }, {});

  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const getCurrentTime = () => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    return `${hours}:00`;
  };

  return (
    <div className="space-y-6">
      {/* Header + Month Controls */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-nunito text-white">Calendar</h1>
          <p className="mt-1 text-orange-300 font-nunito">Manage your schedule and appointments</p>
        </div>
        <div className="flex gap-3 items-center">
          <button onClick={prevMonth} className="px-3 py-2 bg-white/10 border border-white/10 text-orange-100 rounded-lg hover:bg-white/15 transition font-nunito">◀</button>
          <div className="text-white font-nunito font-semibold">
            {currentMonth.toLocaleString(undefined, { month: 'long', year: 'numeric' })}
          </div>
          <button onClick={nextMonth} className="px-3 py-2 bg白/10 border border白/10 text-orange-100 rounded-lg hover:bg白/15 transition font-nunito">▶</button>
          <button onClick={today} className="px-3 py-2 bg-white/10 border border-white/10 text-orange-100 rounded-lg hover:bg-white/15 transition font-nunito">Today</button>
          <button
            onClick={() => {
              setNewEvent({ 
                title: '', 
                description: '', 
                date: getTodayDate(), 
                time: getCurrentTime(), 
                type: 'meeting', 
                attendeeEmail: '' 
              });
              setShowEventModal(true);
            }}
            className="px-4 py-2 bg-amber-500 text-black font-semibold rounded-lg hover:bg-amber-600 transition font-nunito flex items-center gap-2"
          >
            <span>➕</span>
            Add Event
          </button>
        </div>
      </div>

      {/* Month Grid */}
      <div className="bg-orange-900/40 border border-white/10 rounded-2xl p-4">
        <div className="grid grid-cols-7 gap-2 mb-2 text-orange-200 font-nunito">
          {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
            <div key={d} className="text-center text-sm">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-2">
          {/* Empty cells before first day */}
          {Array.from({ length: startDayOfWeek }).map((_, i) => (
            <div key={`empty-${i}`} className="h-32 bg-black/20 border border-white/5 rounded-lg" />
          ))}
          {/* Days of month */}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const dateObj = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i + 1);
            const key = formatDateKey(dateObj);
            const dayEvents = eventsByDate[key] || [];
            const isToday = new Date().toDateString() === dateObj.toDateString();
            return (
              <div 
                key={key} 
                onClick={(e) => {
                  // Only open modal if clicking empty space (not an event)
                  if (e.target.closest('.event-item')) return;
                  setNewEvent({ 
                    title: '', 
                    description: '', 
                    date: key, 
                    time: getCurrentTime(), 
                    type: 'meeting', 
                    attendeeEmail: '' 
                  });
                  setShowEventModal(true);
                }}
                className={`h-32 bg-black/30 border ${isToday ? 'border-amber-500/50' : 'border-white/10'} rounded-lg p-2 flex flex-col cursor-pointer hover:bg-black/40 transition`}
              >
                <div className="flex items-center justify-between">
                  <span className={`text-xs font-nunito ${isToday ? 'text-amber-300' : 'text-orange-200'}`}>{i + 1}</span>
                  {isToday && <span className="text-[10px] text-amber-400 font-nunito">Today</span>}
                </div>
                <div className="mt-1 space-y-1 overflow-auto">
                  {dayEvents.slice(0,3).map(ev => (
                    <div 
                      key={ev.id} 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEventClick(ev);
                      }}
                      className="event-item text-xs rounded-md px-2 py-1 flex items-center gap-2 cursor-pointer hover:bg-amber-500/20 transition"
                      style={{ backgroundColor: 'rgba(245, 158, 11, 0.15)', border: '1px solid rgba(245, 158, 11, 0.35)' }}
                    >
                      <span>{ev.type === 'meeting' ? '👥' : ev.type === 'call' ? '📞' : ev.type === 'deadline' ? '⏰' : '📌'}</span>
                      <span className="truncate text-orange-100 font-nunito">{ev.title}</span>
                    </div>
                  ))}
                  {dayEvents.length > 3 && (
                    <div className="text-[11px] text-amber-300 font-nunito">+{dayEvents.length - 3} more</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Event Modal */}
      {showEventModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-orange-900/90 border border-white/10 rounded-2xl max-w-md w-full">
            <div className="p-6">
              <h2 className="text-2xl font-bold font-nunito text-white mb-6">Create Event</h2>
              <form onSubmit={handleCreateEvent} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-orange-200 font-nunito mb-2">
                    Event Title
                  </label>
                  <input
                    type="text"
                    value={newEvent.title}
                    onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                    required
                    className="w-full px-4 py-2 bg-black/50 border border-white/10 rounded-lg text-white placeholder-orange-400/50 focus:outline-none focus:border-amber-500/50 font-nunito"
                    placeholder="Meeting with client"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-orange-200 font-nunito mb-2">
                    Description
                  </label>
                  <textarea
                    value={newEvent.description}
                    onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                    rows="3"
                    className="w-full px-4 py-2 bg-black/50 border border-white/10 rounded-lg text-white placeholder-orange-400/50 focus:outline-none focus:border-amber-500/50 font-nunito"
                    placeholder="Event details..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-orange-200 font-nunito mb-2">
                    Attendee Email (Optional)
                  </label>
                  <input
                    type="email"
                    value={newEvent.attendeeEmail}
                    onChange={(e) => setNewEvent({ ...newEvent, attendeeEmail: e.target.value })}
                    className="w-full px-4 py-2 bg-black/50 border border-white/10 rounded-lg text-white placeholder-orange-400/50 focus:outline-none focus:border-amber-500/50 font-nunito"
                    placeholder="client@example.com"
                  />
                  <p className="text-xs text-orange-300/70 mt-1 font-nunito">
                    If provided, an invitation with a Google Meet link will be sent.
                  </p>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-orange-200 font-nunito">
                      Date
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        const selectedDate = new Date(newEvent.date);
                        setCurrentMonth(selectedDate);
                        setShowEventModal(false);
                      }}
                      className="text-xs text-amber-400 hover:text-amber-300 font-nunito underline"
                    >
                      View this month
                    </button>
                  </div>
                  <input
                    type="date"
                    value={newEvent.date}
                    onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                    required
                    className="w-full px-4 py-2 bg-black/50 border border-white/10 rounded-lg text-white focus:outline-none focus:border-amber-500/50 font-nunito"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-orange-200 font-nunito mb-2">
                    Time
                  </label>
                  <input
                    type="time"
                    value={newEvent.time}
                    onChange={(e) => setNewEvent({ ...newEvent, time: e.target.value })}
                    required
                    className="w-full px-4 py-2 bg-black/50 border border-white/10 rounded-lg text-white focus:outline-none focus:border-amber-500/50 font-nunito"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-orange-200 font-nunito mb-2">
                    Type
                  </label>
                  <select
                    value={newEvent.type}
                    onChange={(e) => setNewEvent({ ...newEvent, type: e.target.value })}
                    className="w-full px-4 py-2 bg-black/50 border border-white/10 rounded-lg text-white focus:outline-none focus:border-amber-500/50 font-nunito"
                  >
                    <option value="meeting">Meeting</option>
                    <option value="call">Call</option>
                    <option value="deadline">Deadline</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="submit"
                    className="flex-1 py-2 px-4 bg-amber-500 text-black font-semibold rounded-lg hover:bg-amber-600 transition font-nunito"
                  >
                    Create Event
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowEventModal(false)}
                    className="px-4 py-2 bg-white/10 border border-white/10 text-orange-100 rounded-lg hover:bg-white/15 transition font-nunito"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Event Detail Modal with RSVP */}
      {showEventDetailModal && selectedEvent && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-orange-900/90 border border-white/10 rounded-2xl max-w-2xl w-full">
            <div className="p-6">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold font-nunito text-white">{selectedEvent.title}</h2>
                  <p className="text-sm text-orange-300 font-nunito mt-1">
                    {new Date(selectedEvent.date).toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })} at {selectedEvent.time}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowEventDetailModal(false);
                    setSelectedEvent(null);
                  }}
                  className="text-orange-300 hover:text-white transition"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                {selectedEvent.description && (
                  <div>
                    <h3 className="text-sm font-semibold text-orange-200 font-nunito mb-2">Description</h3>
                    <p className="text-white font-nunito">{selectedEvent.description}</p>
                  </div>
                )}

                {selectedEvent.meetLink && (
                  <div>
                    <h3 className="text-sm font-semibold text-orange-200 font-nunito mb-2">Meeting Link</h3>
                    <a 
                      href={selectedEvent.meetLink} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-amber-400 hover:text-amber-300 underline font-nunito break-all"
                    >
                      {selectedEvent.meetLink}
                    </a>
                  </div>
                )}

                {selectedEvent.attendees && selectedEvent.attendees.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-orange-200 font-nunito mb-2">Attendees</h3>
                    <div className="space-y-2">
                      {selectedEvent.attendees.map((attendee, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-black/30 rounded-lg px-3 py-2">
                          <span className="text-white font-nunito">{attendee.email}</span>
                          <span className={`text-xs px-2 py-1 rounded font-nunito ${
                            attendee.responseStatus === 'accepted' ? 'bg-green-500/20 text-green-300' :
                            attendee.responseStatus === 'declined' ? 'bg-red-500/20 text-red-300' :
                            attendee.responseStatus === 'tentative' ? 'bg-yellow-500/20 text-yellow-300' :
                            'bg-gray-500/20 text-gray-300'
                          }`}>
                            {attendee.responseStatus === 'accepted' ? '✓ Yes' :
                             attendee.responseStatus === 'declined' ? '✗ No' :
                             attendee.responseStatus === 'tentative' ? '? Maybe' :
                             'No Response'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <h3 className="text-sm font-semibold text-orange-200 font-nunito mb-3">Going?</h3>
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleRSVP('accepted')}
                      className="flex-1 py-3 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition font-nunito"
                    >
                      ✓ Yes
                    </button>
                    <button
                      onClick={() => handleRSVP('tentative')}
                      className="flex-1 py-3 px-4 bg-yellow-600 hover:bg-yellow-700 text-white font-semibold rounded-lg transition font-nunito"
                    >
                      ? Maybe
                    </button>
                    <button
                      onClick={() => handleRSVP('declined')}
                      className="flex-1 py-3 px-4 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition font-nunito"
                    >
                      ✗ No
                    </button>
                  </div>
                </div>

                {selectedEvent.htmlLink && (
                  <div className="pt-4 border-t border-white/10">
                    <a 
                      href={selectedEvent.htmlLink} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-amber-400 hover:text-amber-300 text-sm font-nunito flex items-center gap-2"
                    >
                      <span>📅</span>
                      View in Google Calendar
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarPage;
